import React, { Component, ReactNode } from 'react';
import { KompisText } from '@inter-ikea-kompis/react-components';
import SkapaIconButton from '@ingka/button';
import { IDexfSettings, ITranslations } from '@inter-ikea-kompis/types';
import ArrowRightIconPaths from '@ingka/ssr-icon/paths/arrow-right';
import ArrowLeftIconPaths from '@ingka/ssr-icon/paths/arrow-left';
import CrossIconPaths from '@ingka/ssr-icon/paths/cross';

import {
	StyledProductIntroGallery,
	ProductIntroGalleryWrapper,
	GalleryWrapper,
	ProductViewWrapper,
	HeadlineWrapper,
	BackButtonWrapper,
	PIG,
} from './ProductIntroGalleryStyle';
import ProductView from './ProductView/ProductView';
import HeaderContainer from '../../containers/HeaderContainer';
import { localeService } from '../../services';
import { SkapaTheme } from '@inter-ikea-kompis/themes';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import {
	GalleryItem,
	Settings,
	Platform,
	WriteDirection,
	DeviceType,
	Orientation,
	ShoppingGalleryItem,
	PredefinedConfiguration,
	ProductCardItem,
	SofaRangeName,
} from 'sofa-reborn-react';
import ProductCardModalEnum from '@inter-ikea-kompis/component-product-card/lib/enums/ProductCardModalEnum';
import SkapaText from '@ingka/text';

type Props = {
	platform: Platform;
	deviceType: DeviceType;
	orientation: Orientation;
	settings: Settings;
	writeDirection: WriteDirection;
	galleryItems: GalleryItem[];
	predefinedConfigurations?: PredefinedConfiguration[];
	startFromScratchImageSrc: string;
	startFromScratchElementIndex: number;
	showScreensaver: boolean;
	isInUpptackaPlatform: boolean;
	hasVisitScene: boolean;
	galleryShoppingProducts: ShoppingGalleryItem[];
	kompisSettings?: IDexfSettings | null;
	kompisTranslations?: ITranslations | null;
	showProductIntroGallery: boolean;
	nrPerRow: number;
	nrOfEmptyElements: number;
	visibleModal: ProductCardModalEnum | null;
	useLocalIds: boolean;
	ecoFeeSheetVisibleId: string | null;
	showCoverOnlyCard: boolean;
	isProduction: boolean;
	sofaRangeName?: SofaRangeName;
	onModalOpen?: (e: CustomEvent, productId: string) => void;
	onModalClose?: () => void;
	onSelectItem?: (item: ProductCardItem, showCoverPicker: boolean) => void;
	onStartFromScratchClick?: () => void;
	onWrapperClick?: () => void;
	onClose?: () => void;
	onCoverOnlyClick?: () => void;
};

/**
 * React component.
 *
 */
export default class ProductIntroGallery extends Component<Props> {
	/**
	 * React render method.
	 *
	 * @returns React node.
	 */
	public render(): ReactNode {
		const { props } = this;
		const {
			onSelectItem,
			onStartFromScratchClick,
			onClose,
			onWrapperClick,
			onModalOpen,
			onModalClose,
			onCoverOnlyClick,
		} = props;

		return (
			<>
				{props.showProductIntroGallery && (
					<PIG platform={props.platform}>
						<ProductIntroGalleryWrapper id="productIntroGallery" platform={props.platform}>
							<StyledProductIntroGallery>
								<GalleryWrapper>
									<HeaderContainer onSummaryPage={false} />
									<ProductViewWrapper
										platform={props.platform}
										deviceType={props.deviceType}
										onClick={(): void => onWrapperClick && onWrapperClick()}
										id="product-view"
									>
										<HeadlineWrapper deviceType={props.deviceType} orientation={props.orientation}>
											<SkapaText tagName={'h2'} headingSize={'m'}>
												{localeService.getTranslation('sprGalleryHeadline')}
											</SkapaText>
											{props.hasVisitScene && !props.isInUpptackaPlatform ? (
												<BackButtonWrapper
													deviceType={props.deviceType}
													writeDirection={props.writeDirection}
													orientation={props.orientation}
													onClick={(): void => onClose && onClose()}
												>
													{props.deviceType === 'desktop' || props.deviceType === 'tablet' ? (
														<KompisText
															theme={SkapaTheme}
															type={ThemeFontStyleTypeEnum.bodyLarge}
															// @ts-expect-error Kompis elemenets support style but it isn't exposed
															style={
																props.writeDirection === 'ltr'
																	? { marginRight: '8px', cursor: 'pointer' }
																	: { marginLeft: '8px', cursor: 'pointer' }
															}
														>
															{localeService.getTranslation('sprGalleryGoToBuildView')}
														</KompisText>
													) : null}
													<SkapaIconButton
														iconOnly
														ssrIcon={
															props.deviceType === 'mobile'
																? CrossIconPaths
																: props.writeDirection === 'ltr'
																? ArrowRightIconPaths
																: ArrowLeftIconPaths
														}
														small={true}
														type={'secondary'}
														style={{ flexShrink: '0' }}
														aria-label={localeService.getTranslation('sprGalleryGoToBuildView')}
													/>
												</BackButtonWrapper>
											) : null}
										</HeadlineWrapper>
										{props.kompisSettings && props.kompisTranslations && (
											<ProductView
												platform={props.platform}
												deviceType={props.deviceType}
												orientation={props.orientation}
												kompisSettings={props.kompisSettings}
												kompisTranslations={props.kompisTranslations}
												showCoverOnlyCard={props.showCoverOnlyCard}
												galleryItems={props.galleryShoppingProducts}
												predefinedConfigurations={props.predefinedConfigurations}
												startFromScratchImageSrc={props.startFromScratchImageSrc}
												startFromScratchElementIndex={props.startFromScratchElementIndex}
												nrPerRow={props.nrPerRow}
												nrOfEmptyElements={props.nrOfEmptyElements}
												visibleModal={props.visibleModal}
												useLocalIds={props.useLocalIds}
												ecoFeeSheetVisibleId={props.ecoFeeSheetVisibleId}
												sofaRangeName={props.sofaRangeName}
												isProduction={props.isProduction}
												onModalOpen={(e: CustomEvent, productId: string): void =>
													onModalOpen && onModalOpen(e, productId)
												}
												onModalClose={onModalClose}
												onItemClick={(item: ProductCardItem, showCoverPicker: boolean): void =>
													onSelectItem && onSelectItem(item, showCoverPicker)
												}
												onStartFromScratchClick={(): void =>
													onStartFromScratchClick && onStartFromScratchClick()
												}
												onCoverOnlyClick={(): void => onCoverOnlyClick && onCoverOnlyClick()}
											/>
										)}
									</ProductViewWrapper>
								</GalleryWrapper>
							</StyledProductIntroGallery>
						</ProductIntroGalleryWrapper>
					</PIG>
				)}
			</>
		);
	}
}
